import React, {useEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {Chess, DEFAULT_POSITION} from "chess.js";
import socket from "../utils/clientSocket";
import ChessClock from "../ChessClock";
import GameOverModal from "./GameOverModal";
import "../App.css";
import EndGameModal from "./EndGameModal";
import ChessBoard from "./ChessBoard.jsx";
import Chat from "./Chat.jsx";
import Moves from "./Moves";
import {useUser} from "../UserContext";
import UserLink from "./UserLink";

// Game state constants
export const gameState = {
    NOT_STARTED: "not started",
    IN_PROGRESS: "in progress",
    FINISHED: "finished",
};

const Game = () => {
    const location = useLocation();
    const state = location.state;
    const user = useUser();
    const [opponent, setOpponent] = useState(state.opponent)

    // Board and game state
    const initialFen = state.initialFen || DEFAULT_POSITION;
    const [fen, setFen] = useState(state.fen || initialFen);
    const [gameId, setGameId] = useState(state.gameId);
    const [orientation, setOrientation] = useState(state.orientation);
    const [currentTurn, setCurrentTurn] = useState(state.currentTurn);
    const [gameLength, setGameLength] = useState(state.gameLength);
    const [currentGameState, setGameState] = useState(state.gameState);
    const [winner, setWinner] = useState(state.winner);
    const [eloDeltas, setEloDeltas] = useState(state.eloDeltas);
    const [moves, setMoves] = useState(state.moves || [])
    //This seems like double work, but it is because we need to access moves inside useEffect at some point, but we do
    //not want to keep re-registering our event listeners
    const movesRef = useRef(state.moves || [])

    // This ref holds the player’s color
    const color = useRef(state.color);
    //This game ref is not actually used to perform moves, but it gives us an easy way of moving back and forth
    //in the moves made
    const gameRef = useRef(new Chess(initialFen))
    const moveNumberRef = useRef(0)

    const navigate = useNavigate();

    // Flip board orientation
    const toggleOrientation = () => {
        setOrientation((prev) => (prev === "white" ? "black" : "white"));
    };

    const addToMoveTracking = (move) => {
        try {
            gameRef.current.move(move);
            moveNumberRef.current++;
        } catch (e) {
            //This is hacky, but currently we are double moving everything (because it's being emitted by the socket afterwards)
            //This causes the gameRef to throw an error due to invalid moves. By catching here we only allow valid moves (
            // all moves should already have been validated), so the only thing this should do is prevent the double moving from
            //ruining the moveNumberRef and gameRef
            //TODO consider a cleaner solution
        }

    }

    const handleMove = (fen, move) => {
        setFen(fen);
        addToMoveTracking(move);
        socket.emit("move", { move, gameId });
    };

    const nextMove = () => {
        if (moveNumberRef.current < movesRef.current.length) {
            gameRef.current.move(movesRef.current[moveNumberRef.current]);
            moveNumberRef.current++;
            setFen(gameRef.current.fen())
        }
    }

    const previousMove = () => {

        if (moveNumberRef.current > 0) {
            moveNumberRef.current--;
            gameRef.current.undo();
            setFen(gameRef.current.fen())
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === "ArrowRight") {
            nextMove()
        }

        if (e.key === "ArrowLeft") {
            previousMove();
        }
    }

    useEffect(() => {
        movesRef.current = moves
    }, [moves]);

    useEffect(() => {
        //on refresh we need to include any potential moves made in the gameRef (so we can go properly back and forth)
        movesRef.current.forEach((move) => addToMoveTracking(move))
        // Use the gameOver event (shows GameOverModal)
        socket.on("gameOver", (data) => {
            setFen(data.fen);
            setWinner(data.winningColor);
            setGameState(gameState.FINISHED);

            //This is kinda hacky, but it ensures that we keep the state when refreshing the page
            const newState = {...state, fen: data.fen, winner: data.winningColor, gameState: gameState.FINISHED, moves: movesRef.current}

            if(data.move) {
                newState.moves.push(data.move)
                setMoves((prevMoves) => [...prevMoves, data.move])
            }

            navigate(location.pathname, {state: newState})
        });

        // Board updates
        socket.on("update", (gameData) => {
            setFen(gameData.fen);
            setCurrentTurn(gameData.currentTurn);
            //If an invalid move is somehow attempted, this might be null
            if(gameData.move) {
                setMoves((prevMoves) => [...prevMoves, gameData.move])
                addToMoveTracking(gameData.move);
            }
        });

        //Usually we navigate here from frontpage when a "gameStarted" event is sent. But in the case of a refresh we
        //need to listen here as well
        // Use the gameStarted event
        socket.on("gameStarted", listenForRejoinGame)

        window.addEventListener("keydown", handleKeyDown)

        return () => {
            socket.off("gameOver");
            socket.off("update");
            socket.off("gameStarted", listenForRejoinGame);
            window.removeEventListener("keydown", handleKeyDown)
        };
    }, []);

    const listenForRejoinGame = ({gameId, gameLength, isWhite, fen, currentTurn, eloDeltas, moves, opponent}) => {
        const newState = {}
        newState.gameId = gameId
        newState.fen = fen
        newState.gameLength = gameLength;
        newState.gameState = gameState.IN_PROGRESS;
        newState.winner = null
        newState.currentTurn = currentTurn;
        newState.eloDeltas = eloDeltas;
        newState.moves = moves;
        newState.opponent = opponent
        movesRef.current = moves;
        setFen(fen)
        setGameId(gameId)
        setGameLength(gameLength)
        setGameState(gameState.IN_PROGRESS)
        setWinner(null)
        setCurrentTurn(currentTurn)
        setEloDeltas(eloDeltas)
        setMoves(moves)
        setOpponent(opponent)

        //It may look like a mistake that we set this to the actual length - but when we are at the latest move
        //we do not access the moves array directly. Plus, if we go to previous move from this state, the number is
        //decremented, and then going to the next move from there will correctly read the latest move.
        moveNumberRef.current = moves.length
        gameRef.current = new Chess(initialFen)

        //we do not need to listen for anything in initial fen, as either this is a rematch or reload of match.
        //in both cases initial fen should be the same, so just ensure we dont override to null on re-navigation
        newState.initialFen = initialFen;
        moves.forEach((move) => gameRef.current.move(move))

        // Determine if the current socket/player is white
        newState.orientation = isWhite ? "white" : "black";
        newState.color = isWhite ? "w" : "b";
        setOrientation(newState.orientation);
        color.current = newState.color;

        navigate(location.pathname, {state: newState})
    };


    const showUserInfo = (eloDeltas) => {
        return (
            <div className="modal-content" style={{fontFamily: "monospace", width: "auto", color: "#666"}}>
                <div style={{fontWeight: "700"}}>
                    <UserLink user={user} style={{marginTop: "0", marginBottom: "0"}}/>
                    <p style={{marginTop: "0", marginBottom: "0"}}>vs</p>
                    <UserLink user={opponent} style={{marginTop: "0", marginBottom: "0"}}/>
                </div>
                <div style={{fontSize: "0.8rem", fontStyle: "italic"}}>
                    <p style={{marginBottom: "0"}}>Win/Draw/Loss</p>
                    <p style={{marginTop: "0"}}>
                        {eloDeltas.winEloDelta}/{eloDeltas.drawEloDelta}/{eloDeltas.lossEloDelta}
                    </p>
                </div>
            </div>
        );
    };


    return (
        <div
            style={{
                display: "flex",
                gap: "6rem",
                marginTop: "3rem",
                paddingBottom: "10rem"
            }}
        >
            {/* Left column: Elo display and Chatbox */}
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    marginLeft: "2rem"
                }}
            >
                <div>{showUserInfo(eloDeltas)}</div>
                <div
                    className="box"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        marginTop: "1rem",
                        boxShadow: "0 0 10px rgba(0, 0, 0, 0.15)",
                        borderRadius: "0.5rem",
                        backgroundColor: "#fafafa",
                        padding: "0.5rem",
                        height: "15.4rem"
                    }}
                >
                    <Chat
                        style={{width: "14rem"}} chatType={"game"} chatId={gameId}
                    />
                </div>
            </div>

            {/* Center column: Chessboard, clocks, and board-flip toggle */}
            <div
                style={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <ChessBoard
                    isLiveGame={true}
                    color={color.current}
                    fen={fen}
                    moveCallback={handleMove}
                    orientation={orientation}
                />
                {/* Top clock */}
                <div style={{ position: "absolute", top: "-4.5rem", right: "0" }}>
                    <ChessClock
                        key="top-clock-ingame"
                        gameState={currentGameState}
                        gameLength={
                            orientation === "white" ? gameLength?.b : gameLength?.w
                        }
                        color={orientation === "white" ? "b" : "w"}
                        turn={currentTurn}
                    />
                </div>
                {/* Bottom clock */}
                <div style={{ position: "absolute", bottom: "-5.5rem", right: "0" }}>
                    <ChessClock
                        key="bottom-clock-ingame"
                        gameState={currentGameState}
                        gameLength={
                            orientation === "white" ? gameLength?.w : gameLength?.b
                        }
                        color={orientation === "white" ? "w" : "b"}
                        turn={currentTurn}
                    />
                </div>
                {/* Toggle orientation button */}
                <div
                    style={{
                        position: "absolute",
                        bottom: "0.3rem",
                        right: "-1.8rem",
                        cursor: "pointer",
                    }}
                >
                    <img
                        src="swap_symbol.png"
                        alt="Swap Board Orientation"
                        className="icon-style"
                        onClick={toggleOrientation}
                        title="Flip Board"
                    />
                </div>
                {/* Game over modal overlay */}
                {winner && (
                    <div
                    >
                        <GameOverModal
                            winner={winner}
                            color={color.current}
                            gameId={gameId}
                        />
                    </div>
                )}
            </div>

            {/* Right column: Moves list and side-by-side buttons */}
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "14rem",
                    gap: "1rem",
                    alignSelf: "stretch",
                    height: "28.1rem"
                }}
            >

                <Moves moves={moves.slice(0, moveNumberRef.current)}
                       initialFen={initialFen}/>
                <div

                >
                    <EndGameModal gameId={gameId} />

                </div>
            </div>
        </div>
    );
};

export default Game;
